import "./HotTakeAiPrompt.scss";

import { Alert, Button, Flex, Text } from "@fluentui/react-northstar";
import { HotTakeGenre } from "@pollyai/types";
import FormState, { FormData } from "@shopify/react-form-state";
import { SurveyLimits } from "@teams/cards";
import { HOT_TAKE_GENRES } from "@teams/client-shared";
import { Question, QuestionType } from "@teams/types";
import { useState } from "react";

import { useUserInfo } from "../../../../../_shared/actions/useUserInfo";
import LlmLoader from "../../../../../_shared/assets/llm-loader.gif";
import { TextAreaField } from "../../../../../_shared/components/fields/TextAreaField";
import { PollyIcon } from "../../../../../_shared/components/PollyIcon";
import {
  extractFetchErrorMessage,
  isError,
  isFetchError,
} from "../../../../../_shared/services/ChannelApi";
import { GeneratedHotTakeResponse, llmApi } from "../../../../../_shared/services/LlmApi";
import { useTeamsContext } from "../../../../../_shared/state/TeamsContextProvider";
import { clientEventTracker } from "../../../../../_shared/utils/clientTelemetry";

export const HotTakeAiPrompt = ({
  onClose,
}: {
  onClose: (question?: Question<QuestionType>) => void;
}) => {
  const { teamsContext } = useTeamsContext();
  const [generatedHotTakes, setGeneratedHotTakes] = useState<GeneratedHotTakeResponse>();
  const { user } = useUserInfo();

  const initialValues = {
    aiPrompt: "",
  };

  const submit = async (form: FormData<{ aiPrompt: string }>) => {
    const { fields } = form;

    clientEventTracker.track("submit prompt clicked", user, {
      type: "hot-take",
      promptText: fields.aiPrompt.value,
    });

    const result = await llmApi.generateHotTakeFromPrompt(teamsContext, fields.aiPrompt.value);

    if (isError(result)) {
      const message = isFetchError(result) ? extractFetchErrorMessage(result) : result.message;
      return [{ message: `Failed to generate Hot Take: ${message}` }];
    }

    setGeneratedHotTakes(result);
  };

  let title = "Prompt Hot Take with AI";
  let instructions =
    "Use prompt input below to generate a unique Hot Take on whatever topic you think your audience would enjoy debating:";
  let buttonTitle = "Submit";
  if (generatedHotTakes) {
    title = "AI Hot Takes";
    instructions =
      "Choose from the generated Hot Takes below or adjust your prompt for different options.";
    buttonTitle = "Resubmit Prompt";
  }

  return (
    <FormState initialValues={initialValues} onSubmit={submit}>
      {({ fields, submit, submitting, errors }) => {
        if (submitting) {
          return (
            <Flex column gap="gap.medium">
              <Flex vAlign="center" gap="gap.small">
                <PollyIcon icon="ai-magic-icon" size="large" inheritColor />
                <Text content={title} weight="bold" size="large" />
              </Flex>
              <img className="hot-take-ai-prompt__loading" src={LlmLoader} alt="Loading..." />
            </Flex>
          );
        }

        return (
          <Flex column gap="gap.medium">
            <Flex vAlign="center" gap="gap.small">
              <PollyIcon icon="ai-magic-icon" size="large" inheritColor />
              <Text content={title} weight="bold" size="large" />
            </Flex>

            <Text className="hot-take-ai-prompt__instructions" content={instructions} />

            <Flex column gap="gap.smaller" className="hot-take-ai-prompt__prompt">
              <Text content="Enter your prompt" weight="bold" />

              <TextAreaField
                field={fields.aiPrompt}
                placeholder="Enter your topic, e.g. Pizza, Cats, or Sports"
                maxLength={SurveyLimits.Questions.Choices.Label.MaxLength}
                rows={3}
                fluid
                resize="none"
                autoFocus
              />

              <Flex hAlign="end" gap="gap.small">
                {!generatedHotTakes && <Button content="Cancel" onClick={() => onClose()} />}
                <Button
                  primary={!generatedHotTakes}
                  icon={<PollyIcon icon="ai-magic-icon" inheritColor />}
                  content={buttonTitle}
                  onClick={() => {
                    submit();
                  }}
                  disabled={!fields.aiPrompt.value || submitting}
                />
              </Flex>

              {errors?.length > 0 &&
                errors.map(e => <Alert key={e.message} danger content={e.message} />)}
            </Flex>

            {generatedHotTakes && (
              <HotTakesGeneratedList
                generatedHotTakes={generatedHotTakes}
                onSelectHotTake={(question, genre) => {
                  clientEventTracker.track("use hot-take clicked", user, {
                    type: genre,
                  });

                  onClose(question);
                }}
              />
            )}
          </Flex>
        );
      }}
    </FormState>
  );
};

const HotTakesGeneratedList = ({
  generatedHotTakes,
  onSelectHotTake,
}: {
  generatedHotTakes: GeneratedHotTakeResponse;
  onSelectHotTake: (question: Question<QuestionType>, genre: HotTakeGenre) => void;
}) => {
  const genreViewMapping: Record<HotTakeGenre, string> = {
    funny: "😂 Funny",
    funniest: "🤣 Funniest",
    spicy: "🌶️ Spicy",
    normal: "🍦 Vanilla",
  };

  return (
    <>
      {HOT_TAKE_GENRES.map(genre => {
        const hotTake = generatedHotTakes[genre][0];
        return (
          <Flex column gap="gap.medium" key={genre} className="hot-take-ai-prompt__generated">
            <Flex space="between">
              <Text
                className="hot-take-ai-prompt__generated__title"
                content={genreViewMapping[genre]}
              />
              <Button
                primary
                size="small"
                content="Use Hot Take"
                onClick={() => onSelectHotTake(hotTake, genre)}
              />
            </Flex>
            <Text
              className="hot-take-ai-prompt__generated__label"
              content={`💬 ${hotTake.label}`}
            />
          </Flex>
        );
      })}
    </>
  );
};
